import axios from 'axios';
import classNames from 'classnames';
import Button from 'components/base/Button';
import { Formik } from 'formik';
import { errorHandler } from 'helpers/response-utils';
import { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { publicSettingsValidationSchema } from 'static/validations/publicSettingsValidationSchema';
import * as yup from 'yup';

const PrivateSettingsEditPopup = ({
  handleCloseEditModal,
  settingsID
}: {
  handleCloseEditModal: (value: any) => void;
  settingsID: string;
}) => {
  const nav = useNavigate();
  const token = localStorage.getItem('token');
  const schema = yup.object().shape(publicSettingsValidationSchema);
  const [initialValues, setInitialValues] = useState({
    name: '',
    value: '',
    description: ''
  });

  const fetchData = async () => {
    const response = await axios.get(
      `${process.env.REACT_APP_ENDPOINT}/private-settings/${settingsID}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    errorHandler(response);
    if (response.data['status'] == 'error') {
      toast.error(response.data['message']);
      return false;
    }
    setInitialValues(response.data['data']['setting']);
  };

  useEffect(() => {
    fetchData();
  }, [settingsID]);

  const onSubmit = async (values: any) => {
    const data = {
      value: values.value,
      description: values.description
    };
    const response = await axios.put(
      `${process.env.REACT_APP_ENDPOINT}/private-settings/${settingsID}`,
      data,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    errorHandler(response);
    if (response.data['status'] == 'error') {
      toast.error(response.data['message']);
      return false;
    }
    toast.success(response.data['message']);
    closeEditModal();
  };

  const closeEditModal = () => {
    handleCloseEditModal(false);
  };
  return (
    <Row>
      <Col xl={12}>
        <Formik
          validationSchema={schema}
          onSubmit={values => onSubmit(values)}
          initialValues={initialValues}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            touched,
            errors,
            setValues
          }) => {
            useEffect(() => {
              setValues({
                name: initialValues.name,
                value: '',
                description: initialValues.description
              });
            }, [initialValues]);
            return (
              <Form noValidate onSubmit={handleSubmit}>
                <Row className={classNames('g-3', 'mb-6')}>
                  <Form.Group className="mb-2">
                    <Form.Label>Name</Form.Label>
                    <Form.Control type="text" value={values.name} disabled />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Value</Form.Label>
                    {/* auto height adjustment */}
                    <Form.Control
                      as="textarea"
                      name="value"
                      style={{ height: 'auto' }}
                      rows={4}
                      value={values.value}
                      onChange={handleChange}
                      isValid={touched.value && !errors.value}
                      isInvalid={touched.value && !!errors.value}
                    />
                    <Form.Control.Feedback type="invalid">
                      {' '}
                      {errors.value}{' '}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      rows={4}
                      isValid={touched.description && !errors.description}
                      isInvalid={touched.description && !!errors.description}
                    />
                    <Form.Control.Feedback type="invalid">
                      {' '}
                      {errors.description}{' '}
                    </Form.Control.Feedback>
                  </Form.Group>
                  <div className="text-end">
                    <Button
                      variant="phoenix-primary"
                      className="me-2 px-6"
                      onClick={closeEditModal}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" variant="primary">
                      Save
                    </Button>
                  </div>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Col>
    </Row>
  );
};

export default PrivateSettingsEditPopup;
